// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * ui.ts — the menus, results and modals. Plain DOM, a handful of screens.
 */

import { el, escapeHtml, fmt } from './dom';
import { MODES, modeOf, type Mode } from './modes';
import { Game } from './game';
import { BOT_PRESETS, botPlay } from './bot';
import { makeRng, hashSeed } from '@ben-gy/game-engine/rng';
import type { PlayerResult } from './runner';

export const HOW_TO_HTML = `
  <p>A brick drifts down from the top. <strong>Slide</strong> to steer it left and
  right, <strong>tap</strong> it (or ↑) to spin, and hold <strong>Drop</strong>
  (or Space/↓) to bring it down faster. For fine aiming, the <strong>◀ ▶ Nudge</strong>
  buttons shift the falling brick a brick-width at a time. The moment it touches the
  pile it locks in — the bricks are real, so they fall, lean and slide, and nothing
  snaps to a grid.</p>
  <p>Build as tall as you dare before the whole tower tips over.</p>
  <ul>
    <li><strong>Ascent</strong> — unlimited bricks, race to the height line. A topple just costs you time.</li>
    <li><strong>Salvage</strong> — twelve bricks, no line. Every one that slides off is gone for good.</li>
    <li><strong>Spire</strong> — the plinth is barely a brick wide. No safe base — balance is everything.</li>
  </ul>
  <p>Playing friends? Place bricks well to earn a <strong>Gust</strong> — spend it to
  blow a crosswind at whoever's tower is highest. It only nudges the brick they're
  dropping, never their settled tower, so it can rattle a lead but never topple it for them.</p>
  <p>Desktop: ← → steer · Z / X or ↑ spin · Space / ↓ drop faster · P pause · G gust.</p>`;

export const ABOUT_HTML = `
  <p><strong>Kilter</strong> is a physics tower game. There is no grid — every brick
  is a rigid body with real mass and friction, so the tower is <em>actually</em>
  balanced, not notionally balanced, and you lose by toppling.</p>
  <p>Play solo, chase the daily tower, or race up to six friends over a private link.
  Multiplayer is peer-to-peer: your browser talks straight to your friends' browsers.
  A public signalling relay only helps them find each other for the first handshake —
  no game data is ever stored on a server, and each player's tower is simulated only
  on their own device.</p>
  <p>No accounts, no cookies, no tracking beyond an anonymous page-view count.</p>
  <p>Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>
  · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more games, tools &amp; sites</a>.</p>`;

// ------------------------------------------------------------------- modal ---
export function openModal(title: string, html: string): void {
  const back = el('div', 'modal-back');
  const card = el('div', 'modal-card');
  card.innerHTML = `<h2>${escapeHtml(title)}</h2><div class="modal-body">${html}</div>`;
  const close = el('button', 'btn');
  close.type = 'button';
  close.textContent = 'Close';
  close.addEventListener('click', () => back.remove());
  card.appendChild(close);
  back.appendChild(card);
  back.addEventListener('click', (e) => { if (e.target === back) back.remove(); });
  document.body.appendChild(back);
}

// ------------------------------------------------------------------ menu ---
export interface MenuOpts {
  container: HTMLElement;
  mode: Mode;
  best: number;
  muted: boolean;
  onMode: (m: Mode) => void;
  onSolo: () => void;
  onRace: () => void;
  onDaily: () => void;
  onFriends: () => void;
  onHowTo: () => void;
  onAbout: () => void;
  onMute: (m: boolean) => void;
}

export function renderMenu(o: MenuOpts): void {
  const { container } = o;
  container.innerHTML = '';
  const box = el('div', 'menu');

  const title = el('div', 'title');
  title.innerHTML = `<h1>Kilter</h1><p class="tag">Stack bricks that really fall — build tall before the whole thing tips.</p>`;
  box.appendChild(title);

  const modeWrap = el('div', 'mode-select');
  for (const m of MODES) {
    const b = el('button', `mode-card${m.id === o.mode.id ? ' on' : ''}`);
    b.type = 'button';
    b.innerHTML = `<span class="mc-name">${m.name}</span><span class="mc-blurb">${m.blurb}</span>`;
    b.addEventListener('click', () => o.onMode(m));
    modeWrap.appendChild(b);
  }
  box.appendChild(modeWrap);

  if (o.best > 0) {
    const best = el('div', 'best');
    best.innerHTML = `Best in ${o.mode.name}: <strong>${fmt(o.best)}</strong> cm`;
    box.appendChild(best);
  }

  const play = el('div', 'menu-actions');
  const solo = el('button', 'btn primary'); solo.type = 'button'; solo.textContent = 'Play solo';
  solo.addEventListener('click', o.onSolo);
  const race = el('button', 'btn'); race.type = 'button'; race.textContent = 'Race the bots';
  race.addEventListener('click', o.onRace);
  const daily = el('button', 'btn'); daily.type = 'button'; daily.textContent = 'Daily tower';
  daily.addEventListener('click', o.onDaily);
  const friends = el('button', 'btn'); friends.type = 'button'; friends.textContent = 'Play with friends';
  friends.addEventListener('click', o.onFriends);
  play.append(solo, race, daily, friends);
  box.appendChild(play);

  const links = el('div', 'menu-links');
  const how = el('button', 'link-btn'); how.type = 'button'; how.textContent = 'How to play';
  how.addEventListener('click', o.onHowTo);
  const about = el('button', 'link-btn'); about.type = 'button'; about.textContent = 'About';
  about.addEventListener('click', o.onAbout);
  const mute = el('button', 'link-btn'); mute.type = 'button'; mute.textContent = o.muted ? 'Sound: off' : 'Sound: on';
  mute.addEventListener('click', () => o.onMute(!o.muted));
  links.append(how, about, mute);
  box.appendChild(links);

  container.appendChild(box);
}

// ------------------------------------------------------------- results ---
export interface ResultsOpts {
  container: HTMLElement;
  results: PlayerResult[];
  mode: Mode;
  seed: number;
  multiplayer: boolean;
  benchmark: number | null;   // a clean-line height in cm
  best: number;
  tally: Record<string, number>;
  waitingText?: string;
  onAgain: () => void;
  onLobby?: () => void;
  onMenu: () => void;
  onShare: () => void;
  onFeedback: () => void;
}

export function renderResults(o: ResultsOpts): void {
  const { container } = o;
  container.innerHTML = '';
  const box = el('div', 'results');

  const rows = [...o.results].sort((a, b) => b.score - a.score);
  const top = rows[0];

  const head = el('div', 'res-head');
  const me = o.results.find((r) => r.isSelf);
  if (o.multiplayer) {
    const iWon = top && me && top.id === me.id;
    head.innerHTML = `<h2>${iWon ? 'You built highest!' : `${escapeHtml(top?.name ?? 'Nobody')} built highest`}</h2>`;
  } else {
    const won = me?.won;
    head.innerHTML = won
      ? `<h2>Reached the line!</h2><p class="res-sub">${me?.heightCm} cm in ${me?.drops} bricks</p>`
      : `<h2>${me?.heightCm ?? 0} cm</h2><p class="res-sub">${o.mode.name}${me && me.lost > 0 ? ` · ${me.lost} brick${me.lost > 1 ? 's' : ''} lost` : ''}</p>`;
  }
  box.appendChild(head);

  if (!o.multiplayer && o.best > 0) {
    const b = el('div', 'res-note');
    b.innerHTML = me && me.heightCm >= o.best ? '<strong>New best!</strong>' : `Best: ${fmt(o.best)} cm`;
    box.appendChild(b);
  }
  if (!o.multiplayer && o.benchmark !== null) {
    const bm = el('div', 'res-note muted');
    bm.textContent = `A clean line reaches about ${o.benchmark} cm on this tower.`;
    box.appendChild(bm);
  }

  // Every player's breakdown (principle #9).
  const table = el('div', 'res-table');
  for (const r of rows) {
    const row = el('div', `res-row${r.isSelf ? ' me' : ''}${r === top && o.multiplayer ? ' win' : ''}`);
    const wins = o.multiplayer ? (o.tally[r.id] ?? 0) : 0;
    row.innerHTML = `
      <span class="rr-name">${escapeHtml(r.name)}${r.isSelf ? ' <em>(you)</em>' : ''}${r.left ? ' <span class="rr-left">left</span>' : ''}${r.isBot ? ' <span class="rr-bot">bot</span>' : ''}</span>
      <span class="rr-h">${r.heightCm}<small>cm</small></span>
      <span class="rr-det">${r.won ? '<b class="rr-goal">reached</b> · ' : ''}${r.drops} placed · ${r.lost} lost</span>
      ${o.multiplayer ? `<span class="rr-tally">${wins}</span>` : ''}`;
    table.appendChild(row);
  }
  box.appendChild(table);

  if (o.waitingText) {
    const w = el('div', 'res-waiting');
    w.innerHTML = `<span class="spinner"></span> ${escapeHtml(o.waitingText)}`;
    box.appendChild(w);
  }

  const actions = el('div', 'res-actions');
  const again = el('button', 'btn primary'); again.type = 'button';
  again.textContent = o.multiplayer ? 'Play again' : 'Again';
  again.addEventListener('click', o.onAgain);
  actions.appendChild(again);
  if (o.multiplayer && o.onLobby) {
    const lob = el('button', 'btn'); lob.type = 'button'; lob.textContent = 'Back to lobby';
    lob.addEventListener('click', o.onLobby);
    actions.appendChild(lob);
  }
  const share = el('button', 'btn'); share.type = 'button'; share.textContent = o.multiplayer ? 'Share room' : 'Share tower';
  share.addEventListener('click', o.onShare);
  actions.appendChild(share);
  const menu = el('button', 'btn'); menu.type = 'button'; menu.textContent = 'Menu';
  menu.addEventListener('click', o.onMenu);
  actions.appendChild(menu);
  box.appendChild(actions);

  const fb = el('button', 'link-btn results-feedback'); fb.type = 'button'; fb.textContent = 'Feedback';
  fb.addEventListener('click', o.onFeedback);
  box.appendChild(fb);

  container.appendChild(box);
}

// ------------------------------------------------------------- benchmark ---
/** A deterministic clean line on this exact tower — what a tidy build reaches. */
export function benchmarkFor(mode: Mode, seed: number): number {
  const g = new Game({ mode, seed });
  const rng = makeRng((seed ^ 0xbeef) >>> 0);
  let t = 0;
  while (!g.over && t < 60) { botPlay(g, BOT_PRESETS.steady, rng, t); t += 1; }
  return g.heightCm;
}

// ------------------------------------------------------------- share/seed ---
export function shareLink(mode: Mode, seed: number): string {
  const u = new URL(location.origin + location.pathname);
  u.searchParams.set('mode', mode.id);
  u.searchParams.set('seed', String(seed >>> 0));
  return u.toString();
}

export function readShareParams(): { mode: Mode | null; seed: number | null } {
  const u = new URL(location.href);
  const seedStr = u.searchParams.get('seed');
  const modeStr = u.searchParams.get('mode');
  const seed = seedStr !== null && /^\d+$/.test(seedStr) ? Number(seedStr) >>> 0 : null;
  return { mode: modeStr ? modeOf(modeStr) : null, seed };
}

export function clearShareParams(): void {
  const u = new URL(location.href);
  u.searchParams.delete('mode');
  u.searchParams.delete('seed');
  history.replaceState(null, '', u.toString());
}

export function dailySeed(): number {
  const d = new Date();
  const key = `${d.getUTCFullYear()}-${d.getUTCMonth() + 1}-${d.getUTCDate()}`;
  return hashSeed(`kilter-${key}`) >>> 0;
}